import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DataTransferService } from '../data-transfer.service';
import { DataTransferOperation } from '../data-transfer.interfaces';
import { UtilsService } from '../../../../shared/utils.service';

@Component({
    standalone: false,
    selector: 'app-data-transfer-progress-dialog',
    templateUrl: './data-transfer-progress-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./data-transfer-progress-dialog.component.scss']
})
export class DataTransferProgressDialogComponent implements OnInit, OnDestroy {

    /**
     * How often the dialog polls the status endpoint, in milliseconds.
     */
    private static readonly POLL_INTERVAL_MS: number = 3_000;

    /**
     * After this much wall-clock time with no new progress data written by the
     * worker, the dialog surfaces a "may be stalled" banner. Set to 2 minutes so
     * bursty imports (long entity with no inter-entity write) don't false-alarm
     * but a truly dead worker is still visible to the admin.
     */
    private static readonly STALL_THRESHOLD_MS: number = 120_000;

    accountId: number;
    operationType: string;
    operation: DataTransferOperation | null = null;
    pollingInterval: any;
    showWarnings: boolean = false;
    showErrors: boolean = false;

    /**
     * True when the operation has been in `processing` longer than STALL_THRESHOLD_MS
     * without the `updated_at` advancing. Used to surface a warning banner.
     */
    stalled: boolean = false;

    /**
     * Last observed `updated_at` value, used to detect a stall.
     */
    private lastUpdatedAt: string | null = null;
    private lastUpdatedObservedAt: number = Date.now();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private dialogRef: MatDialogRef<DataTransferProgressDialogComponent>,
        private dataTransferService: DataTransferService,
        private utils: UtilsService
    ) {
        this.accountId = data.accountId;
        this.operationType = data.operationType;
    }

    ngOnInit(): void {
        this.startPolling();
    }

    ngOnDestroy(): void {
        this.stopPolling();
    }

    private startPolling(): void {
        // Fetch immediately, then poll on an interval.
        this.fetchStatus();
        this.pollingInterval = setInterval(() => {
            this.fetchStatus();
        }, DataTransferProgressDialogComponent.POLL_INTERVAL_MS);
    }

    private stopPolling(): void {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
    }

    private fetchStatus(): void {
        this.dataTransferService.getStatus(this.accountId).then((response: any) => {
            this.operation = response?.data || null;

            this.refreshStallState();

            // Stop polling when the operation reaches a terminal state.
            if (this.operation && (this.operation.status === 'completed' || this.operation.status === 'failed')) {
                this.stopPolling();
            }
        }).catch(() => {
            // Continue polling on transient network errors; the next tick will retry.
        });
    }

    /**
     * Check whether the operation is making progress. A stall is declared when
     * the server's `updated_at` hasn't moved in STALL_THRESHOLD_MS while the
     * status is still `processing` or `queued`.
     */
    private refreshStallState(): void {
        if (!this.operation || (this.operation.status !== 'processing' && this.operation.status !== 'queued')) {
            this.stalled = false;
            return;
        }

        const currentUpdatedAt: string | null = this.operation.updated_at ?? null;
        if (currentUpdatedAt !== this.lastUpdatedAt) {
            this.lastUpdatedAt = currentUpdatedAt;
            this.lastUpdatedObservedAt = Date.now();
            this.stalled = false;
            return;
        }

        const elapsed: number = Date.now() - this.lastUpdatedObservedAt;
        this.stalled = elapsed >= DataTransferProgressDialogComponent.STALL_THRESHOLD_MS;
    }

    onClose(): void {
        this.dialogRef.close();
    }

    onDownload(): void {
        if (this.operation && this.operation.type === 'export' && this.operation.status === 'completed') {
            this.dataTransferService.getDownloadInfo(this.accountId, this.operation.id)
                .then((response: any) => {
                    if (!response?.data?.url) {
                        this.utils.showToast('The export file is no longer available.');
                        return;
                    }
                    this.utils.onDownloadFromTemporaryURL(response.data.url, response.data.custom_file_name);
                })
                .catch(() => {
                    this.utils.showToast('Failed to download the export file.');
                });
        }
    }

    getElapsedTime(): string {
        if (!this.operation?.result_data?.elapsed) {
            return '-';
        }
        const seconds = Math.round(this.operation.result_data.elapsed);
        if (seconds < 60) {
            return `${seconds}s`;
        }
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
    }
}
